/**
 * The ring an incoming call makes.
 *
 * THREE WAYS TO RING, and the hook's job is picking one. Oscillators, which is
 * what a browser uses and what this file builds below: a ring is a few tones
 * and a rhythm, which is less code than the fetch that would load one. The
 * shipped file (`public/ring.wav`), which is the same ring rendered ahead of
 * time, because the desktop app cannot use an oscillator - see `fileRing.ts`.
 * And the operator's own upload, which can only ever be a file.
 *
 * It is also now the ONLY ring. The Voice SDK plays one of its own, an mp3 off
 * `sdk.twilio.com`, and until `audio/apply.ts` switched that off the two of them
 * played over each other - see `silenceSdkRing`. Being the only one is what
 * makes the device choice below mean anything: with two rings out of two output
 * paths, routing one of them still leaves the other on the laptop speaker.
 *
 * ⚠ It is allowed to fail silently, and that is a decision. An incoming call
 * arrives with no user gesture behind it by definition, so a browser that has
 * never been clicked in refuses to start audio - the promise rejects and there
 * is nothing to be done about it. The visual side of the incoming card is
 * therefore the real notification and the sound is the bonus, never the other
 * way round.
 *
 * What that paragraph did NOT license was building a fresh `AudioContext` here
 * on every ring. One built at the moment a call arrives has no gesture behind
 * it, comes up `suspended`, and then schedules oscillators against a clock that
 * does not move: no sound, no rejection, nothing to see. The context now comes
 * from `audio/context.ts`, where it was unlocked by the first click anywhere in
 * the CRM, and `readyAudioContext` refuses to hand back one that is not
 * running. The sound is still the bonus; it is no longer a bonus that quietly
 * never arrives.
 */
import { useEffect, useRef } from "react";
import { openAudioSink } from "../audio/sink";
import { readAudioChoice } from "../audio/choice";
import { readAudioVolume } from "../audio/volume";
import { readyAudioContext } from "../audio/context";
import { startFileRing } from "./fileRing";
import { hasCustomRing, ringSource } from "./ringSource";
import {
  CYCLE_SEC,
  GAIN,
  RAMP_SEC,
  RING_BURSTS,
  warbleAt,
  WARBLE_SEC,
  type Ring,
} from "./ringSpec";
import { isDesktopApp } from "../../../lib/desktop";


/**
 * Start ringing on `deviceId`, or on the system default when it is "".
 *
 * Async for two reasons now. The routing is one: an `AudioContext` cannot be
 * pointed at a device directly, so the graph ends in a stream on an `<audio>`
 * element whose sink is set, and `setSinkId` and `play()` are both promises.
 * The other is the context itself - it may need waking, and waking has to
 * finish BEFORE anything is scheduled, or the first burst is queued against a
 * frozen `currentTime` and never plays. The first burst therefore lands a few
 * milliseconds after the card does, which is under the threshold at which
 * anybody notices a phone rang late.
 */
async function startRing(deviceId: string): Promise<Ring | null> {
  const ctx = await readyAudioContext();
  // No audio in this browser, or a context that could not be woken. The card
  // carries the call, and `ringGate` is what tells the operator why it is quiet.
  if (!ctx) return null;
  let stopped = false;

  const sink = await openAudioSink(ctx, deviceId);

  const burst = () => {
    // `running`, not `!== "closed"`. The shared context can be suspended under
    // us mid-ring by the OS or by tab throttling, and scheduling into a
    // suspended context is the exact silence this file was rewritten to end.
    if (stopped || ctx.state !== "running") return;
    // Read per burst, not once at the top. A ring lasts as long as somebody
    // takes to reach the phone, which is exactly long enough to turn it down -
    // and a slider that only takes effect from the NEXT call is a slider that
    // does nothing at the one moment anybody reaches for it.
    const peak = GAIN * readAudioVolume().ring;
    if (peak <= 0) return;
    const now = ctx.currentTime;

    for (const shape of RING_BURSTS) {
      const at = now + shape.at;
      const gain = ctx.createGain();
      gain.connect(sink.node);
      // Ramped rather than switched: a square-edged gain change is an audible
      // click at the start and end of every single burst.
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(peak, at + RAMP_SEC);
      gain.gain.setValueAtTime(peak, at + shape.dur - RAMP_SEC);
      gain.gain.linearRampToValueAtTime(0, at + shape.dur);

      // One oscillator per burst, stepping between the two tones, rather than
      // two oscillators sounding together. Together they would be a chord; the
      // point of a telephone ring is that it alternates.
      const osc = ctx.createOscillator();
      osc.type = "sine";
      for (let offset = 0; offset < shape.dur; offset += WARBLE_SEC) {
        osc.frequency.setValueAtTime(warbleAt(offset), at + offset);
      }
      osc.connect(gain);
      osc.start(at);
      osc.stop(at + shape.dur);
    }
  };

  burst();
  const timer = window.setInterval(burst, CYCLE_SEC * 1000);
  return {
    stop: () => {
      stopped = true;
      window.clearInterval(timer);
      sink.dispose();
      // The context is NOT closed. It is shared and `close()` is one-way, so
      // hanging up on the first call of the day would leave every later ring,
      // test beep and level meter silent for the rest of the session. The nodes
      // this ring created are already finished - each oscillator was given a
      // stop time when it was started - and the sink above is the only thing
      // that had to be let go of.
    },
  };
}

/**
 * Ring while `active` is true. Silent when the operator has asked for reduced
 * motion - the same preference covers "do not surprise me", and the incoming
 * card is still perfectly visible.
 *
 * The output device is read from storage here rather than passed in: the card
 * that mounts this has nothing to say about which speaker the office phone
 * rings on, and the operator's answer is already written down.
 */
export function useRingtone(active: boolean): void {
  const ringRef = useRef<Ring | null>(null);

  useEffect(() => {
    if (!active) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    // The ring can be torn down before it has finished being built: a caller
    // who gives up after two seconds unmounts this while `setSinkId` is still
    // in flight, and without the flag that ring would start into an empty room
    // and keep going.
    let cancelled = false;
    // Two reasons to ring from a file. An uploaded ringtone can only be played
    // as a file, and the desktop app can only ring from one at all, because an
    // oscillator there needs a gesture the app may never get. Everywhere else
    // the oscillator is smaller and needs no request.
    //
    // A refusal falls back to the oscillator rather than to nothing. In a
    // browser that is the autoplay policy talking, and the oscillator path
    // knows how to wait for a click; the operator then hears the default trill
    // instead of their own sound, which is the right way round for a phone.
    const device = readAudioChoice().ring;
    const fromFile = hasCustomRing() || isDesktopApp();
    const play = fromFile
      ? () => startFileRing(ringSource(), device).then((ring) => ring ?? startRing(device))
      : () => startRing(device);

    void play()
      .then((ring) => {
        if (!ring) return;
        if (cancelled) {
          ring.stop();
          return;
        }
        ringRef.current = ring;
      })
      .catch(() => {
        // Autoplay policy, or no audio device. The card carries the call.
      });

    return () => {
      cancelled = true;
      ringRef.current?.stop();
      ringRef.current = null;
    };
  }, [active]);
}
